import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import Header from './component/Header';
import { API_URL } from '../config';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PublicFooter from '../components/layout/PublicFooter';
import { Link, useParams } from 'react-router-dom';

const RenewalHistory = () => {
  const { plateNumber } = useParams();
  const [plateRenewals, setPlateRenewals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flashMessage, setFlashMessage] = useState('');

  useEffect(() => {
    const storedFlashMessage = localStorage.getItem('flashMessage');
    if (storedFlashMessage) {
      setFlashMessage(storedFlashMessage);
      localStorage.removeItem('flashMessage');
    }
  }, []);

  useEffect(() => {
    loadPlateRenewalsHistoryData();
  }, []);

  const loadPlateRenewalsHistoryData = () => {
    const authToken = localStorage.getItem('token');
    if (!authToken) return;

    fetch(API_URL + `/renewal-history/${plateNumber}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((response) => response.json())
      .then((data) => {
        setPlateRenewals(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
        setLoading(false);
      });
  };

  const hideFlashMessage = () => setFlashMessage('');

  const statusBadge = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'completed' || s === 'success') return 'tw-bg-green-100 tw-text-green-700';
    if (s === 'pending') return 'tw-bg-yellow-100 tw-text-yellow-700';
    if (s === 'failed' || s === 'error') return 'tw-bg-red-100 tw-text-red-700';
    return 'tw-bg-gray-100 tw-text-gray-700';
  };

  return (
    <div className="tw-min-h-screen tw-flex tw-flex-col tw-bg-gray-50">
      <Header />

      <div className="tw-flex-1 tw-max-w-7xl tw-mx-auto tw-w-full tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-py-8">
        {/* Flash message */}
        {flashMessage && (
          <div className="tw-mb-6 tw-bg-green-50 tw-border tw-border-green-200 tw-rounded-lg tw-px-4 tw-py-3 tw-flex tw-items-center tw-justify-between">
            <span className="tw-text-sm tw-text-green-800">{flashMessage}</span>
            <button onClick={hideFlashMessage} className="tw-text-green-600 hover:tw-text-green-800 tw-border-0 tw-bg-transparent tw-cursor-pointer">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        )}

        {/* Page header */}
        <div className="tw-mb-8">
          <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-500 tw-mb-2">
            <Link to="/account" className="tw-text-blue-600 hover:tw-text-blue-700 tw-no-underline">Dashboard</Link>
            <svg className="tw-w-4 tw-h-4 tw-text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            <span>Renewal History</span>
          </div>
          <h1 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-m-0">
            Renewal History — <span className="tw-text-blue-600">{plateNumber}</span>
          </h1>
        </div>

        {/* Table */}
        <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm">
          <div className="tw-overflow-x-auto">
            <table className="tw-w-full">
              <thead>
                <tr className="tw-border-b tw-border-gray-200 tw-bg-gray-50">
                  <th className="tw-text-left tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Municipality</th>
                  <th className="tw-text-left tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Card Holder</th>
                  <th className="tw-text-left tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Transaction Date</th>
                  <th className="tw-text-left tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Transaction ID</th>
                  <th className="tw-text-left tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Payment Detail</th>
                  <th className="tw-text-left tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Status</th>
                  <th className="tw-text-right tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Amount</th>
                  <th className="tw-text-left tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Method</th>
                </tr>
              </thead>
              <tbody className="tw-divide-y tw-divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="tw-px-6 tw-py-12 tw-text-center tw-text-sm tw-text-gray-500">
                      <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                        <svg className="tw-animate-spin tw-w-5 tw-h-5 tw-text-blue-500" fill="none" viewBox="0 0 24 24">
                          <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                        </svg>
                        Loading...
                      </div>
                    </td>
                  </tr>
                ) : plateRenewals.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="tw-px-6 tw-py-12 tw-text-center">
                      <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
                        <div className="tw-w-12 tw-h-12 tw-rounded-full tw-bg-gray-100 tw-flex tw-items-center tw-justify-center">
                          <svg className="tw-w-6 tw-h-6 tw-text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        </div>
                        <p className="tw-text-sm tw-text-gray-500 tw-m-0">No renewal history found for this plate</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  plateRenewals.map((data, index) => (
                    <tr key={index} className="hover:tw-bg-gray-50 tw-transition-colors">
                      <td className="tw-px-6 tw-py-4 tw-text-sm tw-text-gray-700">{data.municipality_name}</td>
                      <td className="tw-px-6 tw-py-4 tw-text-sm tw-text-gray-700">{data.card_holder_name}</td>
                      <td className="tw-px-6 tw-py-4 tw-text-sm tw-text-gray-700">{data.transaction_date}</td>
                      <td className="tw-px-6 tw-py-4 tw-text-sm tw-font-mono tw-text-gray-600 tw-text-xs">{data.pay_transaction_id}</td>
                      <td className="tw-px-6 tw-py-4 tw-text-sm tw-text-gray-700">{data.payment_detail}</td>
                      <td className="tw-px-6 tw-py-4">
                        <span className={`tw-inline-block tw-px-2.5 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium ${statusBadge(data.status)}`}>
                          {data.status}
                        </span>
                      </td>
                      <td className="tw-px-6 tw-py-4 tw-text-sm tw-font-medium tw-text-gray-900 tw-text-right">{data.amount}</td>
                      <td className="tw-px-6 tw-py-4 tw-text-sm tw-text-gray-700">{data.payment_method}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ToastContainer />
      <PublicFooter />
    </div>
  );
};

export default RenewalHistory;
